import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface Membership {
  id: string;
  status: string;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  price_amount: number;
  started_at: string | null;
}

const ACTIVE = ["active", "trialing"];

export function useMembership() {
  const [membership, setMembership] = useState<Membership | null>(null);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data: auth } = await supabase.auth.getUser();
    const user = auth.user;
    setUserId(user?.id ?? null);

    if (!user) {
      setMembership(null);
      setLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from("memberships")
      .select("id, status, current_period_end, cancel_at_period_end, price_amount, started_at")
      .eq("user_id", user.id)
      .maybeSingle();

    if (error) {
      console.error("Failed to load membership:", error.message);
    }
    setMembership((data as Membership) ?? null);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const { data: sub } = supabase.auth.onAuthStateChange(() => {
      load();
    });
    return () => sub.subscription.unsubscribe();
  }, [load]);

  const isMember =
    !!membership &&
    ACTIVE.includes(membership.status) &&
    (!membership.current_period_end ||
      new Date(membership.current_period_end) > new Date());

  return { membership, isMember, loading, userId, refresh: load };
}
